// Phase I (I-B, I2): types and hooks for the tenant's subscription and entitlements, matching
// /api/subscription. The API enforces plans; the web only hides what is off.
import { useQuery } from '@tanstack/react-query';
import { matchPath } from 'react-router';
import type { Entitlements, Permission, PlanAddon, PlanFeature, PlanLimit, PlanModule, SubscriptionPreview, SubscriptionStatus } from '@denwen-msp/shared';
import { useAuth, useOptionalAuth } from '@/app/auth';
import { api } from '@/lib/api';

export const ENTITLEMENTS_KEY = ['subscription', 'entitlements'];

/** What this tenant may use. Undefined while loading (show everything until we know). */
export function useEntitlements() {
  // Staff only (the portal has no entitlements route). Optional: TabNav also renders in isolation.
  const user = useOptionalAuth()?.user;
  return useQuery({
    queryKey: ENTITLEMENTS_KEY,
    queryFn: ({ signal }) => api<Entitlements>('/subscription/entitlements', { signal }),
    enabled: user?.kind === 'staff',
    staleTime: 5 * 60_000,
  });
}

/** True unless the plan is known to leave the module out. */
export function useModuleEnabled() {
  const ent = useEntitlements();
  return (m: PlanModule | undefined) => !m || !Array.isArray(ent.data?.modules) || ent.data.modules.includes(m);
}

/**
 * Paths of the web app that belong to a plan module (P2-0, BUG-27). Each is a route pattern
 * matched as a prefix, so `/organizations/:id/monitor` covers every page under it. The most
 * specific pattern wins: `/me/payroll` is payroll, the rest of `/me` is HR.
 *
 * The shell shows the upgrade prompt on these paths, and TabNav and the sidebars hide links to
 * them. Pages a path can't place (in-page tabs, cards) set `planModule` themselves.
 */
export const MODULE_PATHS: Record<PlanModule, string[]> = {
  hr: ['/hr', '/me'],
  payroll: ['/payroll', '/me/payroll', '/hr/statutory-pay', '/admin/payroll-pensions'],
  monitoring: [
    '/monitoring',
    '/organizations/:orgId/monitor',
    '/admin/monitoring-probes',
    '/admin/monitoring-credentials',
    '/admin/monitoring-alerts',
    '/admin/monitoring-templates',
    '/admin/monitoring-event-rules',
  ],
  accounting: ['/accounting', '/payables', '/banking', '/organizations/:orgId/bills', '/admin/open-banking'],
};

const MODULE_PATTERNS = (Object.entries(MODULE_PATHS) as [PlanModule, string[]][])
  .flatMap(([m, patterns]) => patterns.map((path) => ({ m, path, depth: path.split('/').length })))
  .sort((a, b) => b.depth - a.depth);

export function moduleForPath(pathname: string): PlanModule | null {
  return MODULE_PATTERNS.find((p) => matchPath({ path: p.path, end: false }, pathname))?.m ?? null;
}

/**
 * The shared visibility check for navigation (sidebar, palette, New menu, tabs): the user holds
 * the permission, the plan includes the module, and the per-user conditions hold.
 */
export interface Gated {
  permission?: Permission;
  planModule?: PlanModule;
  /** Only for staff users linked to an HR employee record. */
  needsEmployee?: boolean;
  /** Only for platform admins (Denwen MSP's own staff). */
  platformOnly?: boolean;
}

export function useCanSee() {
  const { can, user } = useAuth();
  const moduleEnabled = useModuleEnabled();
  return (g: Gated) =>
    (!g.permission || can(g.permission)) && moduleEnabled(g.planModule) && (!g.needsEmployee || Boolean(user?.employeeId)) && (!g.platformOnly || Boolean(user?.platform));
}

export interface PlanOption {
  key: string;
  name: string;
  description: string | null;
  seatPriceMinor: number | null;
  currency: string;
  interval: 'month' | 'year';
  modules: PlanModule[];
  features: PlanFeature[];
  limits: Partial<Record<PlanLimit, number>>;
  addons: Omit<PlanAddon, 'stripePriceId'>[];
  trialDays: number;
  purchasable: boolean;
}

export interface LastInvoice {
  id: string;
  status: string | null;
  amountDue: number | null;
  amountPaid: number | null;
  currency: string | null;
  hostedUrl: string | null;
  at: number;
}

export interface SubscriptionOverview {
  entitlements: Entitlements;
  subscription: {
    status: SubscriptionStatus;
    seats: number;
    /** Seats the subscription drops to at the next renewal. */
    pendingSeats: number | null;
    addons: string[];
    currentPeriodStart: string | null;
    currentPeriodEnd: string | null;
    cancelAtPeriodEnd: boolean;
    trialEndsAt: string | null;
    hasPaymentMethod: boolean;
    pastDueSince: string | null;
    lastInvoice: LastInvoice | null;
    hasStripeSubscription: boolean;
    hasCustomer: boolean;
  } | null;
  plans: PlanOption[];
  billing: { configured: boolean; mode: 'test' | 'live' };
}

export type { SubscriptionPreview };

/** Minor units → "£12.34". */
export function money(minor: number | null | undefined, currency = 'GBP') {
  if (minor === null || minor === undefined) return '—';
  return new Intl.NumberFormat('en-GB', { style: 'currency', currency: currency.toUpperCase() }).format(minor / 100);
}
